import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import PageLayout from '@/components/layout/PageLayout'
import { apiService } from '@/service/api'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import { useTranslation } from '@/i18n'
import type { VideoBriefSlide, VideoBriefStoryboard } from '@/types/videoBrief'
import StoryboardPanel from './StoryboardPanel'
import { activeSlideIndex } from './clock'

/**
 * Full-page player for a `video` document: a rendered "Explain as video"
 * brief. The mp4 is fetched through the API as a blob (a `<video src>` cannot
 * carry the bearer token) and the storyboard beside it drives the slide list.
 *
 * `?boardName=` is what a board card appends for every viewer; it is read so a
 * brief opened from a board keeps a way back to it.
 */
const VideoBrief = () => {
  const { doc_id } = useParams<{ doc_id: string }>()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const navigateBack = useNavigateBack()
  const boardName = searchParams.get('boardName')
  const videoRef = useRef<HTMLVideoElement | null>(null)

  const [name, setName] = useState<string | null>(null)
  const [mediaUrl, setMediaUrl] = useState<string | null>(null)
  const [mediaError, setMediaError] = useState(false)
  const [storyboard, setStoryboard] = useState<VideoBriefStoryboard | null>(null)
  const [activeIndex, setActiveIndex] = useState(-1)

  useEffect(() => {
    if (!doc_id) return
    let cancelled = false
    let objectUrl: string | null = null
    // Leaving the page aborts the download; a brief is megabytes, not a toast.
    const controller = new AbortController()

    void apiService
      .getDocument(doc_id)
      .then((doc) => {
        if (!cancelled) setName((doc as { name?: string }).name ?? null)
      })
      .catch(() => undefined)

    void apiService
      .getVideoBriefMedia(doc_id, controller.signal)
      .then((blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setMediaUrl(objectUrl)
      })
      .catch(() => {
        if (!cancelled) setMediaError(true)
      })

    // The storyboard is optional: the video plays without a slide list.
    void apiService
      .getVideoBriefStoryboard(doc_id)
      .then((board) => {
        if (!cancelled) setStoryboard(board)
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [doc_id])

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current
    if (!video || !storyboard) return
    setActiveIndex(activeSlideIndex(storyboard.slides, video.currentTime))
  }, [storyboard])

  const handleSeek = useCallback((slide: VideoBriefSlide) => {
    const video = videoRef.current
    if (!video) return
    video.currentTime = slide.start_seconds
    void video.play()?.catch(() => undefined)
  }, [])

  const title =
    name ||
    storyboard?.title ||
    t('document_viewer:video_brief.title', { defaultValue: 'Video brief' })

  return (
    <PageLayout title={title}>
      {boardName && (
        <button
          type="button"
          onClick={navigateBack}
          className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="w-4 h-4 shrink-0" />
          <span className="truncate max-w-[18rem]">{boardName}</span>
        </button>
      )}
      <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-12rem)]">
        <div className="flex-1 min-w-0 bg-black rounded-lg flex items-center justify-center">
          {mediaError ? (
            <p className="text-sm text-white/80 p-4">
              {t('document_viewer:video_brief.load_error', {
                defaultValue: 'The video could not be loaded.',
              })}
            </p>
          ) : mediaUrl ? (
            <video
              ref={videoRef}
              src={mediaUrl}
              controls
              className="w-full h-full rounded-lg"
              onTimeUpdate={handleTimeUpdate}
              data-testid="video-brief-player"
            >
              {t('document_viewer:video_brief.unsupported', {
                defaultValue: 'Your browser cannot play this video.',
              })}
            </video>
          ) : (
            <p className="text-sm text-white/80 p-4">
              {t('document_viewer:video_brief.loading', { defaultValue: 'Loading video...' })}
            </p>
          )}
        </div>
        <aside className="w-full lg:w-80 shrink-0 border rounded-lg overflow-y-auto">
          <h2 className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b">
            {t('document_viewer:video_brief.slides', { defaultValue: 'Slides' })}
          </h2>
          <StoryboardPanel storyboard={storyboard} activeIndex={activeIndex} onSeek={handleSeek} />
        </aside>
      </div>
    </PageLayout>
  )
}

export default VideoBrief
